import React from 'react';
import { Sun, Moon, Contrast, Palette, Check } from 'lucide-react';
import { useConfigStore } from '../store/configStore';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

/**
 * ThemeToggle (T-020, ampliado T-217/T-222)
 * Selector de 10 temas: 5 bases Tailwind (claro/oscuro × contraste) MÁS 5
 * paletas de colores (sepia, oceano, bosque, caramelo, violeta). Lee/escribe
 * `theme`, `contrast` y `palette` en configStore, que aplica la clase `dark` y
 * los atributos `data-contrast`/`data-palette` en <html> (overrides
 * quirúrgicos en contrast.css/palette.css). El trigger reutiliza el mismo
 * botón del toggle original (mismas clases, data-testid, aria-label).
 *
 * Bases (T-020/T-217):
 * 1. Claro                  → theme=light, contrast=normal,  palette=''
 * 2. Gris alto contraste    → theme=light, contrast=alto,    palette=''
 * 3. Oscuro                 → theme=dark,  contrast=normal,  palette=''
 * 4. Oscuro alto contraste  → theme=dark,  contrast=alto,    palette=''
 * 5. Alto contraste extremo → theme=light, contrast=extremo,  palette=''
 *
 * Paletas (T-222):
 * 6.  Perla nacarada → theme=light, palette='sepia'
 * 7.  Océano         → theme=light, palette='oceano'
 * 8.  Bosque         → theme=dark,  palette='bosque'
 * 9.  Caramelo       → theme=dark,  palette='caramelo'
 * 10. Violeta        → theme=dark,  palette='violeta'
 */
const THEMES = [
  { id: 'claro', label: 'Claro', theme: 'light', contrast: 'normal', palette: '' },
  { id: 'claro-ac', label: 'Gris alto contraste', theme: 'light', contrast: 'alto', palette: '' },
  { id: 'oscuro', label: 'Oscuro', theme: 'dark', contrast: 'normal', palette: '' },
  { id: 'oscuro-ac', label: 'Oscuro alto contraste', theme: 'dark', contrast: 'alto', palette: '' },
  { id: 'extremo', label: 'Alto contraste extremo', theme: 'light', contrast: 'extremo', palette: '' },
  // T-222: 5 paletas de colores con combinaciones con sentido para la plataforma.
  // Cada una fija theme (base claro/oscuro) + palette (data-palette en <html>).
  { id: 'sepia', label: 'Perla nacarada (lectura)', theme: 'light', contrast: 'normal', palette: 'sepia' },
  { id: 'oceano', label: 'Océano', theme: 'light', contrast: 'normal', palette: 'oceano' },
  { id: 'bosque', label: 'Bosque', theme: 'dark', contrast: 'normal', palette: 'bosque' },
  { id: 'caramelo', label: 'Caramelo', theme: 'dark', contrast: 'normal', palette: 'caramelo' },
  { id: 'violeta', label: 'Violeta', theme: 'dark', contrast: 'normal', palette: 'violeta' },
];

const ThemeToggle = ({ className = '' }) => {
  const theme = useConfigStore((s) => s.theme);
  const contrast = useConfigStore((s) => s.contrast);
  const palette = useConfigStore((s) => s.palette);
  const setTheme = useConfigStore((s) => s.setTheme);
  const setContrast = useConfigStore((s) => s.setContrast);
  const setPalette = useConfigStore((s) => s.setPalette);

  // Ícono del trigger: paleta activa → Palette; extremo → Contrast;
  // oscuro → Sun; claro → Moon. (Mantiene la señal visual del toggle de T-020.)
  const TriggerIcon = palette
    ? Palette
    : contrast === 'extremo'
      ? Contrast
      : theme === 'dark'
        ? Sun
        : Moon;
  const triggerLabel = palette
    ? 'Selector de tema (paleta activa)'
    : contrast === 'extremo'
      ? 'Selector de tema (alto contraste extremo activo)'
      : 'Selector de tema';

  // Un item está activo cuando coincide theme+contrast+palette. 'extremo'
  // tiene palette '' así que no hay conflicto con la coincidencia por paleta.
  const isActive = (opt) =>
    theme === opt.theme && contrast === opt.contrast && palette === opt.palette;

  // onSelect (idioma Radix DropdownMenuItem) aplica theme + contrast + palette.
  // setContrast va antes que setPalette (extremo tiene palette '' así que no
  // hay conflicto).
  const handleSelect = (opt) => {
    setTheme(opt.theme);
    setContrast(opt.contrast);
    setPalette(opt.palette);
  };

  const renderItem = (opt) => (
    <DropdownMenuItem
      key={opt.id}
      data-testid={`theme-option-${opt.id}`}
      onSelect={() => handleSelect(opt)}
    >
      {/* Marca (Check) en el activo; placeholder opacidad 0 mantiene
          la alineación del texto en los items no activos. */}
      <Check
        className={`w-4 h-4 ${isActive(opt) ? 'opacity-100' : 'opacity-0'}`}
      />
      <span>{opt.label}</span>
    </DropdownMenuItem>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="theme-toggle"
          aria-label={triggerLabel}
          title="Seleccionar tema"
          className={`inline-flex items-center justify-center gap-1.5 h-9 px-2 rounded-md border border-input bg-transparent text-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${className}`}
        >
          <TriggerIcon className="w-5 h-5" />
          {/* Etiqueta visible junto al icono (pedido del dueño); patrón de
              textos-en-iconos de T-206 (oculta en móvil para no saturar). */}
          <span className="hidden md:inline text-xs font-medium">Tema Colores</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {THEMES.slice(0, 5).map(renderItem)}
        {/* T-222: separador visual entre las 5 bases y las 5 paletas de color */}
        <DropdownMenuSeparator />
        {THEMES.slice(5).map(renderItem)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ThemeToggle;
